/**
 * @file App shell header rendering and navigation visibility.
 */

import { appData, state } from "../state/state.js";
import { canAccessView } from "../routing/boardAccess.js";
import { applyBrandingVariables } from "../../shared/ui/brandingTokens.js";

const NAV_ITEMS = [
    { id: 'tasks', label: 'My Tasks' },
    { id: 'patients', label: 'Patients' },
    { id: 'labs', label: 'Labs' },
    { id: 'recalls', label: 'Recalls' },
    { id: 'projects', label: 'Projects' },
    { id: 'assets', label: 'Assets' },
    { id: 'maintenance', label: 'Maintenance' },
    { id: 'compliance', label: 'Compliance' },
    { id: 'ordering', label: 'Ordering' },
    { id: 'drugs', label: 'Controlled Drugs' },
];

function getBrandAsset(kind) {
    const asset = appData.config?.branding?.[kind] || null;
    return asset?.url ? asset : null;
}

function applyConfiguredFavicon() {
    const favicon = getBrandAsset("favicon");
    let link = document.querySelector('link[rel~="icon"]');
    if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
    }

    link.type = favicon?.type || "image/png";
    link.href = favicon?.url || "/favicon.png";
}

function renderMobileMenu(profile, isSettingsActive) {
    const visibleItems = NAV_ITEMS.filter((item) =>
        canAccessView(profile, item.id));
    const isCalendarActive = state.currentView === "calendar";
    const calendarItem = canAccessView(profile, "calendar") ? `
        <button
            type="button"
            id="mobile-calendar-menu-btn"
            class="mobile-menu-footer-action ${isCalendarActive ? 'active' : ''}"
            data-mobile-view="calendar"
            aria-current="${isCalendarActive ? 'page' : 'false'}"
            aria-label="Calendar"
            title="Calendar"
        >
            <span class="material-symbols-outlined text-[20px]">calendar_month</span>
        </button>
    ` : '';
    const settingsItem = profile?.role === 'admin' ? `
        <button
            type="button"
            id="mobile-settings-btn"
            class="mobile-menu-footer-action ${isSettingsActive ? 'active' : ''}"
            data-mobile-action="settings"
            aria-current="${isSettingsActive ? 'page' : 'false'}"
            aria-label="Settings"
            title="Settings"
        >
            <span class="material-symbols-outlined text-[20px]">settings</span>
        </button>
    ` : '';

    return `
        <div id="mobile-app-menu" class="relative md:hidden">
            <button
                type="button"
                id="mobile-menu-toggle"
                class="header-action-btn text-gray-500 transition-colors"
                aria-expanded="false"
                aria-controls="mobile-menu-panel"
                title="Menu"
            >
                <span class="material-symbols-outlined text-2xl">menu</span>
            </button>
            <div id="mobile-menu-panel" class="mobile-menu-panel hidden">
                <div class="py-1">
                    ${visibleItems.map((item) => {
                        const isActive = state.currentView === item.id;
                        return `
                            <button
                                type="button"
                                class="mobile-nav-link ${isActive ? 'active' : ''}"
                                data-mobile-view="${item.id}"
                                aria-current="${isActive ? 'page' : 'false'}"
                            >
                                <span class="min-w-0 flex-1 truncate">${item.label}</span>
                                <span class="mobile-nav-check material-symbols-outlined text-[18px]" aria-hidden="true">check</span>
                            </button>
                        `;
                    }).join('')}
                </div>
                <div class="flex justify-end gap-2 border-t border-gray-100 px-3 py-2">
                    ${calendarItem}
                    ${settingsItem}
                    <button
                        type="button"
                        id="mobile-logout-btn"
                        class="mobile-menu-footer-action"
                        data-mobile-action="logout"
                        aria-label="Logout"
                        title="Logout"
                    >
                        <span class="material-symbols-outlined text-[20px]">logout</span>
                    </button>
                </div>
            </div>
        </div>
    `;
}

function renderDesktopRecallQuickAddButton(profile) {
    if (!canAccessView(profile, "recalls")) {return "";}

    return `
        <button
            id="recall-quick-add-btn"
            class="header-action-btn recall-quick-add-header-btn mr-2 text-gray-500 transition-colors"
            title="Quick Add Recall"
            type="button"
            aria-label="Quick Add Recall"
        >
            <span class="material-symbols-outlined text-2xl">alarm_add</span>
        </button>
    `;
}

function renderDesktopCalendarButton(profile) {
    if (!canAccessView(profile, "calendar")) {return "";}
    const isActive = state.currentView === "calendar";

    return `
        <button
            id="calendar-btn"
            class="header-action-btn mr-2 text-gray-500 transition-colors ${isActive ? 'active' : ''}"
            title="Shared Calendar"
            type="button"
            aria-label="Shared Calendar"
            aria-current="${isActive ? 'page' : 'false'}"
        >
            <span class="material-symbols-outlined text-2xl">calendar_month</span>
        </button>
    `;
}

function syncDesktopCalendarButton(profile) {
    document.getElementById("calendar-btn")?.remove();
    const notificationButton = document.getElementById("notification-bell-btn");
    if (!notificationButton || !canAccessView(profile, "calendar")) {return;}
    notificationButton.insertAdjacentHTML(
        "beforebegin",
        renderDesktopCalendarButton(profile),
    );
}

function syncDesktopRecallQuickAddButton(profile) {
    document.getElementById("recall-quick-add-btn")?.remove();
    const notificationButton = document.getElementById("notification-bell-btn");
    if (!notificationButton || !canAccessView(profile, "recalls")) {return;}
    let actionGroup = document.getElementById("desktop-header-actions");
    if (!actionGroup) {
        actionGroup = document.createElement("div");
        actionGroup.id = "desktop-header-actions";
        actionGroup.className = "ml-auto flex items-center";
        notificationButton.insertAdjacentElement("beforebegin", actionGroup);
        actionGroup.appendChild(notificationButton);
    }
    notificationButton.insertAdjacentHTML(
        "beforebegin",
        renderDesktopRecallQuickAddButton(profile),
    );
}

/**
 * Renders the header section, updating the title and showing/hiding admin buttons.
 * Also manages visibility of navigation links based on user permissions.
 */
export function renderHeader() {
    const mainTitle = document.getElementById('main-title');
    const headerRight = document.getElementById('header-right');
    const appContainer = document.getElementById('app');
    const isSettingsActive = state.currentView === 'settings';
    const headerTopRow = mainTitle?.parentElement;

    if (!headerRight) {return;}

    const profile = state.currentUserProfile;
    const firstName = profile?.name?.split(' ')[0] || 'User';
    if (mainTitle) {
        mainTitle.textContent = `${firstName}'s Dashboard`;
    }
    applyConfiguredFavicon();

    let adminSettingsBtnHtml = '';
    if (profile && profile.role === 'admin') {
        adminSettingsBtnHtml = `<button id="settings-btn" class="header-action-btn text-gray-500 transition-colors ${isSettingsActive ? 'active' : ''}" title="Settings">
            <span class="material-symbols-outlined text-2xl">settings</span>
        </button>`;
    }

    const maintenanceModeActive = profile?.role === 'admin' && (
        appData.config?.notificationFlowBlocked === true ||
        appData.config?.drugsUiLocked === true ||
        appData.config?.nonAdminSignInLocked === true
    );

    if (headerTopRow) {
        headerTopRow.classList.add('relative');
        let maintenanceBanner = document.getElementById('header-maintenance-banner');

        if (maintenanceModeActive) {
            if (!maintenanceBanner) {
                maintenanceBanner = document.createElement('div');
                maintenanceBanner.id = 'header-maintenance-banner';
                headerTopRow.appendChild(maintenanceBanner);
            }
            maintenanceBanner.className = 'absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 items-center rounded-lg border border-[#F4A3A4] bg-[#FDE7E7] px-4 py-2 text-sm font-medium text-[#C95C60] whitespace-nowrap lg:flex';
            maintenanceBanner.textContent = 'System is in maintenance mode';
        } else if (maintenanceBanner) {
            maintenanceBanner.remove();
        }
    }

    applyBrandingVariables(document.documentElement, appData);
    if (appContainer) {
        applyBrandingVariables(appContainer, appData);
    }
    applyBrandingVariables(headerRight, appData);
    headerRight.innerHTML = `
        <div class="flex items-center gap-2 md:hidden">
            ${renderMobileMenu(profile, isSettingsActive)}
        </div>
        <div class="hidden items-center space-x-4 md:flex">
        ${adminSettingsBtnHtml}
        <button id="logout-btn" class="header-action-btn text-gray-500 transition-colors" title="Logout">
            <span class="material-symbols-outlined text-2xl">logout</span>
        </button>
        </div>
    `;
    syncDesktopRecallQuickAddButton(profile);
    syncDesktopCalendarButton(profile);

    document.getElementById('nav-tasks')?.style.setProperty('display', canAccessView(profile, 'tasks') ? 'list-item' : 'none', 'important');
    document.getElementById('nav-patients')?.style.setProperty('display', canAccessView(profile, 'patients') ? 'list-item' : 'none', 'important');
    document.getElementById('nav-projects')?.style.setProperty('display', canAccessView(profile, 'projects') ? 'list-item' : 'none', 'important');
    document.getElementById('nav-assets')?.style.setProperty('display', canAccessView(profile, 'assets') ? 'list-item' : 'none', 'important');
    document.getElementById('nav-labs')?.style.setProperty('display', canAccessView(profile, 'labs') ? 'list-item' : 'none', 'important');
    document.getElementById('nav-maintenance')?.style.setProperty('display', canAccessView(profile, 'maintenance') ? 'list-item' : 'none', 'important');
    document.getElementById('nav-compliance')?.style.setProperty('display', canAccessView(profile, 'compliance') ? 'list-item' : 'none', 'important');
    document.getElementById('nav-recalls')?.style.setProperty('display', canAccessView(profile, 'recalls') ? 'list-item' : 'none', 'important');
    document.getElementById('nav-ordering')?.style.setProperty('display', canAccessView(profile, 'ordering') ? 'list-item' : 'none', 'important');
    document.getElementById('nav-drugs')?.style.setProperty('display', canAccessView(profile, 'drugs') ? 'list-item' : 'none', 'important');
}
